export type OrderQuantities = Record<string, Record<string, number>>;

export interface MenuCategory {
  items: Array<{ id: string; name: string }>;
}

export interface PreparedOrderItem {
  id: string;
  name: string;
  quantity: number;
}

export const removeHiddenQuantities = (
  quantities: OrderQuantities,
  hiddenItems: string[],
): OrderQuantities => {
  const hiddenItemIds = new Set(hiddenItems);

  return Object.fromEntries(
    Object.entries(quantities).map(([dayName, dayQuantities]) => [
      dayName,
      Object.fromEntries(
        Object.entries(dayQuantities).filter(([itemId]) => !hiddenItemIds.has(itemId)),
      ),
    ]),
  );
};

export const removeHiddenOrderItems = <T extends { id: string }>(
  dailyItems: Record<string, T[]>,
  hiddenItems: string[],
): Record<string, T[]> => {
  const hiddenItemIds = new Set(hiddenItems);

  return Object.fromEntries(
    Object.entries(dailyItems)
      .map(([dayName, items]) => [
        dayName,
        items.filter(item => !hiddenItemIds.has(item.id)),
      ] as const)
      .filter(([, items]) => items.length > 0),
  );
};

export const buildVisibleOrderItems = (
  categories: MenuCategory[],
  quantities: OrderQuantities,
  hiddenItems: string[],
  days: string[],
): Record<string, PreparedOrderItem[]> => {
  const hiddenItemIds = new Set(hiddenItems);
  const itemsByDay: Record<string, PreparedOrderItem[]> = {};

  days.forEach(dayName => {
    const dayQuantities = quantities[dayName] || {};
    const items: PreparedOrderItem[] = [];

    categories.forEach(category => {
      category.items.forEach(item => {
        const quantity = dayQuantities[item.id];
        if (!hiddenItemIds.has(item.id) && quantity > 0) {
          items.push({ id: item.id, name: item.name, quantity });
        }
      });
    });

    if (items.length > 0) itemsByDay[dayName] = items;
  });

  return itemsByDay;
};
